(function () {
  var STORAGE_KEY = "en-neume:favorites:v1";
  var ANONYMOUS_ID_KEY = "en-neume:favorites:anonymous-id:v1";
  var ENDPOINT = "/apps/customer-favorites/";
  var productDataCache = new WeakMap();
  var favoriteDetailCache = {};
  var detailRequestCache = {};
  var favoriteDetailMissCache = {};
  var FAVORITE_DETAIL_MISS_TTL = 5 * 60 * 1000;

  // A "miss" means a favorite's product/variant detail could not be loaded.
  // not_found (deleted / unpublished / variant gone) is sticky for the page;
  // fetch_error (transient network / 5xx) is sticky only until the TTL passes,
  // so a temporary outage can recover without a reload.
  function isMissSticky(variantId) {
    var miss = favoriteDetailMissCache[variantId];
    if (!miss) return false;
    if (miss.reason === "not_found") return true;
    return Date.now() - miss.at < FAVORITE_DETAIL_MISS_TTL;
  }

  function favoriteButtons() {
    return Array.prototype.slice.call(
      document.querySelectorAll("[data-en-neume-favorite-toggle]")
    );
  }

  function favoriteLists() {
    return Array.prototype.slice.call(
      document.querySelectorAll("[data-en-neume-favorite-list]")
    );
  }

  function favoriteListScope(list) {
    return list.closest("[data-en-neume-favorite-list-scope]") || document;
  }

  function readLocalFavorites() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];

      var parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];

      return parsed.filter(function (favorite) {
        return favorite && favorite.shopifyVariantId;
      });
    } catch (error) {
      return [];
    }
  }

  function writeLocalFavorites(favorites) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(favorites));
    } catch (error) {
      // Storage can be unavailable (quota exceeded, Safari private mode); the
      // in-memory favorites and the server sync remain the source of truth.
    }
  }

  function readAnonymousCustomerId() {
    var existing = null;
    try {
      existing = window.localStorage.getItem(ANONYMOUS_ID_KEY);
    } catch (error) {
      existing = null;
    }
    if (existing && /^[a-zA-Z0-9_-]{16,80}$/.test(existing)) {
      return existing;
    }

    var next = createAnonymousCustomerId();
    try {
      window.localStorage.setItem(ANONYMOUS_ID_KEY, next);
    } catch (error) {
      // Persisting is best effort; the id stays stable for this page view.
    }
    return next;
  }

  function createAnonymousCustomerId() {
    if (window.crypto && window.crypto.randomUUID) {
      return window.crypto.randomUUID().replace(/-/g, "");
    }

    return (
      String(Date.now()) +
      Math.random().toString(36).slice(2) +
      Math.random().toString(36).slice(2)
    ).slice(0, 40);
  }

  function normalizeImageUrl(url) {
    if (!url) return null;
    if (url.indexOf("//") === 0) return "https:" + url;
    return url;
  }

  function formatMoney(value) {
    if (value === null || value === undefined || value === "") return "";

    var amount = Number(value);
    if (!Number.isFinite(amount)) return "";
    if (amount >= 100000) amount = amount / 100;

    return "¥" + Math.round(amount).toLocaleString("ja-JP");
  }

  function favoriteUrl(favorite) {
    if (!favorite.productHandle) return "#";

    var url = "/products/" + encodeURIComponent(favorite.productHandle);
    if (favorite.shopifyVariantId) {
      url += "?variant=" + encodeURIComponent(favorite.shopifyVariantId);
    }

    return url;
  }

  function favoriteDetail(favorite) {
    return favoriteDetailCache[String(favorite.shopifyVariantId)] || {};
  }

  function isUnavailableFavorite(favorite) {
    var miss = favoriteDetailMissCache[String(favorite && favorite.shopifyVariantId || "")];
    return !!(miss && miss.reason === "not_found");
  }

  function displayFavorites(favorites) {
    return favorites.filter(function (favorite) {
      return !isUnavailableFavorite(favorite);
    });
  }

  function needsFavoriteDetails(favorites) {
    return favorites.some(function (favorite) {
      var variantId = String(favorite && favorite.shopifyVariantId || "");
      return (
        favorite &&
        favorite.productHandle &&
        variantId &&
        !favoriteDetailCache[variantId] &&
        !isMissSticky(variantId)
      );
    });
  }

  function favoriteFromButton(button) {
    return {
      shopifyProductId: Number(button.dataset.shopifyProductId),
      shopifyVariantId: Number(button.dataset.shopifyVariantId),
      productHandle: button.dataset.productHandle || null,
      productTitle: button.dataset.productTitle || null,
      variantTitle: button.dataset.variantTitle || null,
      optionSummary: button.dataset.optionSummary || null,
      imageUrl: normalizeImageUrl(button.dataset.imageUrl || null),
    };
  }

  function mergeFavorites(left, right) {
    var byVariantId = {};

    left.concat(right).forEach(function (favorite) {
      if (!favorite || !favorite.shopifyVariantId) return;
      byVariantId[String(favorite.shopifyVariantId)] = favorite;
    });

    return Object.keys(byVariantId).map(function (variantId) {
      return byVariantId[variantId];
    });
  }

  function hasFavorite(favorites, variantId) {
    return favorites.some(function (favorite) {
      return String(favorite.shopifyVariantId) === String(variantId);
    });
  }

  function removeFavorite(favorites, variantId) {
    return favorites.filter(function (favorite) {
      return String(favorite.shopifyVariantId) !== String(variantId);
    });
  }

  function hydrateFavoriteDetails(favorites) {
    var requests = favorites.map(function (favorite) {
      var variantId = String(favorite.shopifyVariantId || "");
      var handle = favorite.productHandle;
      if (!variantId || !handle || favoriteDetailCache[variantId] || isMissSticky(variantId)) {
        return Promise.resolve();
      }

      // Retrying an expired transient failure: drop the cached failed promise
      // (keyed by handle) so the product JSON is actually re-fetched.
      if (favoriteDetailMissCache[variantId]) {
        delete favoriteDetailMissCache[variantId];
        delete detailRequestCache[handle];
      }

      if (!detailRequestCache[handle]) {
        var sharedUtils = window.ItemManagerThemeUtils;
        detailRequestCache[handle] = sharedUtils && sharedUtils.fetchShopifyProduct
          ? sharedUtils.fetchShopifyProduct(handle)
          : fetch("/products/" + encodeURIComponent(handle) + ".js", {
              method: "GET",
              headers: {
                Accept: "application/json",
              },
              credentials: "same-origin",
            })
              .then(function (response) {
                if (!response.ok) throw new Error("favorite_product_fetch_failed");
                return response.json();
              })
              .catch(function () {
                return null;
              });
      }

      return detailRequestCache[handle].then(function (product) {
        if (!product || !Array.isArray(product.variants)) {
          var utils = window.ItemManagerThemeUtils;
          var notFound = !!(utils && utils.isProductMissing && utils.isProductMissing(handle));
          favoriteDetailMissCache[variantId] = { reason: notFound ? "not_found" : "fetch_error", at: Date.now() };
          return;
        }

        var variant = product.variants.filter(function (item) {
          return String(item.id) === variantId;
        })[0];
        if (!variant) {
          favoriteDetailMissCache[variantId] = { reason: "not_found", at: Date.now() };
          return;
        }

        delete favoriteDetailMissCache[variantId];
        favoriteDetailCache[variantId] = {
          vendor: product.vendor || "",
          title: product.title || favorite.productTitle || "",
          variantTitle: variant.public_title || variant.title || favorite.variantTitle || "",
          imageUrl: normalizeImageUrl(
            (variant.featured_image && variant.featured_image.src) ||
              product.featured_image ||
              favorite.imageUrl
          ),
          price: variant.price,
          compareAtPrice: variant.compare_at_price,
        };
      });
    });

    return Promise.all(requests);
  }

  function readProductData(container) {
    if (!container) return null;
    if (productDataCache.has(container)) return productDataCache.get(container);

    var script = container.querySelector("[data-en-neume-favorite-product-json]");
    if (!script) return null;

    try {
      var data = JSON.parse(script.textContent || "{}");
      productDataCache.set(container, data);
      return data;
    } catch (error) {
      return null;
    }
  }

  function updateButtonVariant(button) {
    var container = button.closest("[data-en-neume-favorite-product]");
    var data = readProductData(container);
    if (!data || !Array.isArray(data.variants)) return;

    var form = button.closest(".product-form") || document.querySelector(".js-product-form");
    var input = form && form.querySelector('input[name="id"]');
    var variantId = input && input.value ? input.value : button.dataset.shopifyVariantId;
    var variant = data.variants.filter(function (item) {
      return String(item.id) === String(variantId);
    })[0];
    if (!variant) return;

    button.dataset.shopifyProductId = data.productId;
    button.dataset.shopifyVariantId = variant.id;
    button.dataset.productHandle = data.handle || "";
    button.dataset.productTitle = data.title || "";
    button.dataset.variantTitle = variant.title || "";
    button.dataset.optionSummary = variant.optionSummary || "";
    button.dataset.imageUrl = variant.imageUrl || data.fallbackImageUrl || "";
  }

  function render(favorites) {
    favoriteButtons().forEach(function (button) {
      updateButtonVariant(button);
      var isFavorited = hasFavorite(favorites, button.dataset.shopifyVariantId);
      var addText = button.querySelector("[data-favorite-add-text]");
      var removeText = button.querySelector("[data-favorite-remove-text]");

      button.classList.toggle("is-favorited", isFavorited);
      button.setAttribute("aria-pressed", isFavorited ? "true" : "false");
      if (addText) addText.hidden = isFavorited;
      if (removeText) removeText.hidden = !isFavorited;
    });

    var displayCount = displayFavorites(favorites).length;
    document.querySelectorAll("[data-en-neume-favorite-count]").forEach(function (count) {
      count.textContent = String(displayCount);
      count.hidden = displayCount === 0;
    });

    renderFavoriteLists(favorites);
  }

  function renderFavoriteLists(favorites) {
    var lists = favoriteLists();
    if (!lists.length) return;

    var visibleFavorites = displayFavorites(favorites);
    lists.forEach(function (list) {
      renderFavoriteList(list, visibleFavorites);
    });

    if (needsFavoriteDetails(favorites)) {
      hydrateFavoriteDetails(favorites).then(function () {
        render(favorites);
      });
    }
  }

  function renderFavoriteList(list, favorites) {
    var scope = favoriteListScope(list);
    var empty = scope.querySelector("[data-en-neume-favorite-empty]");
    var count = scope.querySelector("[data-en-neume-favorite-list-count]");
    list.textContent = "";
    if (count) count.textContent = favorites.length + (favorites.length === 1 ? " item" : " items");
    if (empty) empty.hidden = favorites.length !== 0;
    list.hidden = favorites.length === 0;

    favorites.forEach(function (favorite) {
      var detail = favoriteDetail(favorite);
      var title = detail.title || favorite.productTitle || "商品";
      var imageUrl = detail.imageUrl || favorite.imageUrl;
      var variantTitle = favorite.optionSummary || detail.variantTitle || favorite.variantTitle || "";
      var price = formatMoney(detail.price);
      var compareAtPrice = formatMoney(detail.compareAtPrice);
      var card = document.createElement("product-block");
      var link = document.createElement("a");
      var detailLink = document.createElement("a");
      var imageCont = document.createElement("div");
      var imageLabelWrap = document.createElement("div");
      var media = document.createElement("div");
      var body = document.createElement("div");
      var bodyInner = document.createElement("div");
      var bodyInnerer = document.createElement("div");
      var removeButton = document.createElement("button");

      card.className = "product-block en-neume-favorite-card";
      if (favorite.shopifyProductId) card.dataset.productId = favorite.shopifyProductId;
      if (favorite.productHandle) card.dataset.productHandle = favorite.productHandle;
      link.className = "product-link en-neume-favorite-card__link";
      link.href = favoriteUrl(favorite);
      detailLink.className = "product-link en-neume-favorite-card__detail-link";
      detailLink.href = link.href;
      imageCont.className = "image-cont en-neume-favorite-card__image-cont";
      imageLabelWrap.className = "image-label-wrap";
      media.className = "product-block__image product-block__image--primary product-block__image--active en-neume-favorite-card__media";
      body.className = "product-block__detail align-ltr-left en-neume-favorite-card__body";
      bodyInner.className = "inner";
      bodyInnerer.className = "innerer";
      removeButton.type = "button";
      removeButton.className = "en-neume-favorite-card__remove";
      removeButton.setAttribute("aria-label", title + "をお気に入りから削除");
      removeButton.dataset.enNeumeFavoriteRemove = favorite.shopifyVariantId;
      removeButton.textContent = "×";

      if (imageUrl) {
        var image = document.createElement("img");
        image.src = imageUrl;
        image.alt = title;
        image.loading = "lazy";
        image.className = "theme-img";
        media.appendChild(image);
      }

      var titleNode = document.createElement("p");
      titleNode.className = "product-block__title en-neume-favorite-card__title";
      titleNode.textContent = title;
      detailLink.appendChild(titleNode);

      if (variantTitle && variantTitle !== "Default Title") {
        var variantNode = document.createElement("p");
        variantNode.className = "en-neume-favorite-card__variant";
        variantNode.textContent = variantTitle;
        detailLink.appendChild(variantNode);
      }

      if (price) {
        var priceNode = document.createElement("p");
        priceNode.className = "product-price product-price--block en-neume-favorite-card__price";
        priceNode.textContent = price;
        if (compareAtPrice && compareAtPrice !== price) {
          var compareNode = document.createElement("span");
          compareNode.className = "en-neume-favorite-card__compare-price";
          compareNode.textContent = compareAtPrice;
          priceNode.appendChild(compareNode);
        }
        detailLink.appendChild(priceNode);
      }

      imageLabelWrap.appendChild(media);
      imageCont.appendChild(link);
      link.appendChild(imageLabelWrap);
      bodyInnerer.appendChild(detailLink);
      bodyInner.appendChild(bodyInnerer);
      body.appendChild(bodyInner);
      imageCont.appendChild(removeButton);
      card.appendChild(imageCont);
      card.appendChild(body);
      list.appendChild(card);
    });
  }

  function postFavorite(payload) {
    payload.anonymousCustomerId = anonymousCustomerId;

    return fetch(ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(payload),
      credentials: "same-origin",
    }).then(function (response) {
      if (!response.ok) throw new Error("favorite_sync_failed");
      return response.json();
    });
  }

  var SYNC_QUEUE_KEY = "en-neume:favorites:syncQueue:v1";
  var QUEUE_MIGRATED_KEY = "en-neume:favorites:queue-migrated:v1";
  var queueFlushing = false;
  var opSeq = 0;

  function readSyncQueue() {
    try {
      var raw = window.localStorage.getItem(SYNC_QUEUE_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  function writeSyncQueue(queue) {
    try {
      window.localStorage.setItem(SYNC_QUEUE_KEY, JSON.stringify(queue));
    } catch (error) {
      // Best effort; an unsynced op stays in memory for this page view only.
    }
  }

  function opVariantId(op) {
    if (!op) return "";
    if (op.op === "add") return String((op.favorite && op.favorite.shopifyVariantId) || "");
    return String(op.shopifyVariantId || "");
  }

  // Queue a pending server mutation. Collapses any earlier pending op for the
  // same variant so the most recent intent (last add / remove) wins.
  function enqueueOp(op) {
    op.id = String(Date.now()) + ":" + (opSeq += 1);
    var variantId = opVariantId(op);
    var queue = readSyncQueue().filter(function (existing) {
      return opVariantId(existing) !== variantId;
    });
    queue.push(op);
    writeSyncQueue(queue);
  }

  function dequeueOp(op) {
    var queue = readSyncQueue().filter(function (existing) {
      return existing.id !== op.id;
    });
    writeSyncQueue(queue);
  }

  // Server state with the user's pending ops applied. The server is the source
  // of truth; the queue only carries this user's own not-yet-synced actions.
  function applyQueue(serverFavorites, queue) {
    var result = mergeFavorites(serverFavorites, []);
    queue.forEach(function (op) {
      if (op.op === "add" && op.favorite) {
        result = mergeFavorites(result, [op.favorite]);
      } else if (op.op === "remove") {
        result = removeFavorite(result, op.shopifyVariantId);
      }
    });
    return result;
  }

  function flushSyncQueue() {
    if (queueFlushing) return Promise.resolve();
    if (!readSyncQueue().length) return Promise.resolve();
    queueFlushing = true;
    return flushNextOp();
  }

  function flushNextOp() {
    var queue = readSyncQueue();
    if (!queue.length) {
      queueFlushing = false;
      return Promise.resolve();
    }
    var op = queue[0];
    var payload =
      op.op === "add"
        ? { action: "add", favorite: op.favorite }
        : { action: "remove", shopifyVariantId: op.shopifyVariantId };
    return postFavorite(payload).then(
      function () {
        dequeueOp(op);
        return flushNextOp();
      },
      function () {
        queueFlushing = false; // keep the queue and retry on next load / online
        return null;
      }
    );
  }

  // One-time recovery: favorites added under the previous fire-and-forget client
  // may have silently failed to reach the server. Seed any local-only favorite
  // into the queue as a pending add so switching to a server-authoritative model
  // does not drop them.
  function migrateLocalOnlyToQueue(localFavorites, serverFavorites) {
    var done = false;
    try {
      done = window.localStorage.getItem(QUEUE_MIGRATED_KEY) === "1";
    } catch (error) {
      done = false;
    }
    if (done) return;

    var known = {};
    serverFavorites.forEach(function (favorite) {
      known[String(favorite.shopifyVariantId)] = true;
    });
    readSyncQueue().forEach(function (op) {
      known[opVariantId(op)] = true;
    });
    localFavorites.forEach(function (favorite) {
      var variantId = String(favorite.shopifyVariantId || "");
      if (!variantId || !favorite.shopifyProductId || known[variantId]) return;
      enqueueOp({ op: "add", favorite: favorite, at: Date.now() });
    });

    try {
      window.localStorage.setItem(QUEUE_MIGRATED_KEY, "1");
    } catch (error) {
      // If the flag cannot persist the seeding repeats; the server upsert is
      // idempotent so repeating is harmless.
    }
  }

  function scheduleRender() {
    window.requestAnimationFrame(function () {
      render(favorites);
    });
  }

  function hideLegacyWishlistHero() {
    if (!document.querySelector("[data-en-neume-favorite-list-scope]")) return;

    document
      .querySelectorAll('[id*="hero-wishlist"], [class*="wishlist-hero"], [class*="wishlisthero"]')
      .forEach(function (element) {
        if (element.closest("[data-en-neume-favorite-list-scope]")) return;
        element.style.display = "none";
      });
  }

  function observeLegacyWishlistHero() {
    if (!document.querySelector("[data-en-neume-favorite-list-scope]")) return;

    hideLegacyWishlistHero();

    // Coalesce to one document scan per frame so rendering many wishlist cards
    // (a burst of body mutations) does not run hideLegacyWishlistHero per node.
    var scheduled = false;
    var observer = new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(function () {
        scheduled = false;
        hideLegacyWishlistHero();
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
    window.setTimeout(function () {
      observer.disconnect();
    }, 10000);
  }

  function applyFavoriteCountToHeader() {
    var total = displayFavorites(favorites).length;
    var desired = String(total);
    var shouldHide = total === 0;
    document.querySelectorAll("[data-en-neume-favorite-count]").forEach(function (count) {
      if (count.textContent !== desired) count.textContent = desired;
      if (count.hidden !== shouldHide) count.hidden = shouldHide;
    });
  }

  // The theme re-renders .logo-area__right on cart change (PageHeader.updateFromCartChange),
  // which resets the server-rendered favorite badge to its default (hidden / "0").
  // Re-apply the current favorite count whenever the header subtree is re-rendered.
  function observeHeaderForFavoriteCount() {
    var header = document.querySelector(".section-header") || document.getElementById("pageheader");
    if (!header || typeof MutationObserver === "undefined") return;

    var scheduled = false;
    var observer = new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(function () {
        scheduled = false;
        applyFavoriteCountToHeader();
      });
    });
    observer.observe(header, { childList: true, subtree: true });
  }

  var favorites = readLocalFavorites();
  var anonymousCustomerId = readAnonymousCustomerId();
  observeLegacyWishlistHero();
  render(favorites);
  observeHeaderForFavoriteCount();

  function loadServerFavorites() {
    return fetch(
      ENDPOINT +
        "?anonymousCustomerId=" +
        encodeURIComponent(anonymousCustomerId),
      {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
        credentials: "same-origin",
      }
    )
      .then(function (response) {
        if (!response.ok) throw new Error("favorite_fetch_failed");
        return response.json();
      })
      .then(function (payload) {
        if (
          !payload ||
          (!payload.authenticated && !payload.anonymous) ||
          !Array.isArray(payload.favorites)
        ) {
          return;
        }

        // Server-authoritative: local state is "server state plus this user's
        // own not-yet-synced ops". This stops a stale device from re-uploading
        // (and resurrecting) favorites another device removed.
        migrateLocalOnlyToQueue(favorites, payload.favorites);
        favorites = applyQueue(payload.favorites, readSyncQueue());
        writeLocalFavorites(favorites);
        render(favorites);
        return flushSyncQueue();
      })
      .catch(function () {
        render(favorites);
      });
  }

  // Replay leftover ops from a previous session before reading the server, so
  // the GET reflects them, then load.
  flushSyncQueue().then(loadServerFavorites, loadServerFavorites);

  window.addEventListener("online", function () {
    flushSyncQueue();
  });

  document.addEventListener("click", function (event) {
    var removeButton = event.target.closest("[data-en-neume-favorite-remove]");
    if (removeButton) {
      var removeVariantId = removeButton.dataset.enNeumeFavoriteRemove;
      if (!removeVariantId) return;

      event.preventDefault();
      favorites = removeFavorite(favorites, removeVariantId);
      writeLocalFavorites(favorites);
      render(favorites);
      enqueueOp({
        op: "remove",
        shopifyVariantId: removeVariantId,
        at: Date.now(),
      });
      flushSyncQueue();
      return;
    }

    var button = event.target.closest("[data-en-neume-favorite-toggle]");
    if (!button) return;

    event.preventDefault();
    updateButtonVariant(button);

    var favorite = favoriteFromButton(button);
    var variantId = favorite.shopifyVariantId;
    if (!favorite.shopifyProductId || !variantId) return;

    var nextIsFavorited = !hasFavorite(favorites, variantId);

    if (nextIsFavorited) {
      favorites = mergeFavorites(favorites, [favorite]);
      enqueueOp({ op: "add", favorite: favorite, at: Date.now() });
    } else {
      favorites = removeFavorite(favorites, variantId);
      enqueueOp({ op: "remove", shopifyVariantId: variantId, at: Date.now() });
    }

    writeLocalFavorites(favorites);
    render(favorites);
    flushSyncQueue();
  });

  document.addEventListener("change", function (event) {
    if (event.target && event.target.matches('input[name="id"]')) {
      scheduleRender();
    }
  });

  document.addEventListener("on:variant:change", scheduleRender);
})();
